import { IconBase, IconBaseProps } from 'react-icons';

import { IconManifest } from 'app/agriculture/icons/types';
import { errorColourMain, secondaryColourMain, successColourMain } from 'app/agriculture/material';

interface BatteryBar {
    d: string;
    innerWidth: number;
    innerOffset: number;
}

const batteryIcon: IconManifest & {
    bar: BatteryBar;
} = {
    id: 'battery-icon',
    width: 29,
    height: 14,
    d: 'M24.9998 9.7707c0 1.7049-1.3896 3.0946-3.0947 3.0946H4.24632c-1.70508 0-3.09467-1.3897-3.09467-3.0946V4.22941c0-1.70504 1.38959-3.09455 3.09467-3.09455H21.9051c1.7051 0 3.0947 1.38951 3.0947 3.09455V9.7707ZM21.933 0H4.21846C1.88805 0 0 1.88778 0 4.21281v5.57453C0 12.1123 1.88805 14 4.21846 14H21.933c2.3304 0 4.218-1.8877 4.218-4.21266V4.21281C26.151 1.88778 24.2634 0 21.933 0Zm6.766 6.99768c0 1.30315-.8098 2.40898-1.9573 2.85033V4.15259c1.1475.44123 1.9573 1.54714 1.9573 2.84509Z',
    bar: {
        d: 'M4.7912 12.3116h17.1279c1.4002 0 2.5409-1.1348 2.5409-2.54079V4.22959c0-1.40645-1.1407-2.54116-2.5409-2.54116H4.23086C2.83073 1.68843 1.69 2.82314 1.69 4.22959v5.54122c0 1.40599 1.14073 2.54079 2.54086 2.54079h.56034Z',
        innerWidth: 22.77,
        innerOffset: 1.69,
    },
};

interface BatteryIconProps {
    /**
     * Percentage value between 0 and 100.
     *
     * If set, the battery icon will dynamically display the battery bar to represent the percentage batteryLevel.
     * Green if > 0.7, Red if < 0.3, and Orange otherwise.
     *
     * If not set, the battery bar will be full, and inherit the same color as the rest of the icon.
     */
    batteryLevel?: number;
}

export function BatteryIcon({ batteryLevel, ...props }: IconBaseProps & BatteryIconProps) {
    const { id, d, width, height, bar } = batteryIcon;

    return (
        <IconBase id={id} {...props} viewBox={`0 0 ${width} ${height}`} data-testid="BatteryIcon">
            {batteryLevel == null ? (
                <path d={bar.d} fill="currentColor" />
            ) : (
                <>
                    <defs>
                        <clipPath id="battery-level">
                            <rect
                                x={bar.innerOffset}
                                y={0}
                                // we have a minimum of 2% so that the bar will at least show up red if it's empty
                                width={(bar.innerWidth * Math.max(batteryLevel, 2)) / 100}
                                height={height}
                            />
                        </clipPath>
                    </defs>
                    <path
                        d={bar.d}
                        fill={batteryBarColour(batteryLevel)}
                        clipPath="url(#battery-level)"
                    />
                </>
            )}
            <path d={d} fill="currentColor" />
        </IconBase>
    );
}

function batteryBarColour(batteryLevel: number) {
    if (batteryLevel < 30) return errorColourMain;

    if (batteryLevel > 70) return successColourMain;

    return secondaryColourMain;
}
